/**
 * DepartmentDistributionList - Lista premium de departamentos com distribuição ativa
 * 
 * Design com gradiente, ícone decorativo, efeito shimmer e hover effects
 * Paginação integrada com 2 departamentos por página
 * 
 * Exibe para cada departamento:
 * - Nome
 * - Método de distribuição (badge)
 * - Limite de conversas configurado
 * - Agentes online/total
 * - Último agente que recebeu
 * 
 * @version 1.2.0 - Adicionada paginação
 * @date 2025-12-21
 */

import { useState, useEffect } from 'react';
import { Users, RefreshCw, BarChart3, Zap, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SimplePagination } from '@/components/ui/simple-pagination';
import { cn } from '@/lib/utils';
import type { DepartmentStatus } from '@/hooks/useDistributionMonitor';

// ==============================================================
// Constantes
// ==============================================================

/** Quantidade de departamentos por página */
const PAGE_SIZE = 2;

// ==============================================================
// Interfaces
// ==============================================================

interface DepartmentDistributionListProps {
  /** Lista de departamentos */
  departments: DepartmentStatus[];
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Helpers
// ==============================================================

/** Retorna ícone e label do método de distribuição */
const getMethodInfo = (method: string) => {
  switch (method) {
    case 'round_robin':
      return {
        icon: RefreshCw,
        label: 'Round Robin',
        description: 'Rodízio entre agentes',
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
      };
    case 'least_busy':
      return {
        icon: BarChart3,
        label: 'Menos Ocupado',
        description: 'Prioriza agente com menos conversas',
        color: 'text-purple-500',
        bgColor: 'bg-purple-500/10',
      };
    default:
      return {
        icon: Zap,
        label: method,
        description: 'Método de distribuição',
        color: 'text-primary',
        bgColor: 'bg-primary/10',
      };
  }
};

// ==============================================================
// Componente Principal
// ==============================================================

export function DepartmentDistributionList({ departments, isLoading }: DepartmentDistributionListProps) {
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  
  // Cálculos de paginação
  const totalPages = Math.ceil(departments.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedDepartments = departments.slice(startIndex, startIndex + PAGE_SIZE);
  
  // Reset para página 1 quando lista de departamentos mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [departments.length]);

  // Estado de loading com design premium
  if (isLoading) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Departamentos Ativos</h3>
            <p className="text-xs text-muted-foreground">Distribuição automática</p>
          </div>
        </div>
        
        <div className="p-4 space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-muted/50 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Estado vazio
  if (departments.length === 0) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-semibold text-foreground">Departamentos Ativos</h3>
        </div>
        
        <div className="p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum departamento com distribuição automática ativa
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10 transition-all duration-300 hover:shadow-lg">
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Header premium */}
      <div className="relative flex items-center justify-between p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 transition-transform duration-300 group-hover:scale-110">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Departamentos Ativos</h3>
            <p className="text-xs text-muted-foreground">Distribuição automática habilitada</p>
          </div>
        </div>
        
        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
          {departments.length} ativo{departments.length > 1 ? 's' : ''}
        </Badge>
      </div>
      
      {/* Lista de departamentos paginada */}
      <div className="relative p-4 space-y-3">
        {paginatedDepartments.map(dept => {
          const methodInfo = getMethodInfo(dept.distributionMethod);
          const MethodIcon = methodInfo.icon;

          return (
            <div
              key={dept.id}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg border transition-all duration-200',
                'border-border/50 bg-card/50 hover:bg-card hover:shadow-md hover:-translate-y-0.5'
              )}
            >
              {/* Info do departamento */}
              <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', methodInfo.bgColor)}>
                  <Zap className={cn('h-4 w-4', methodInfo.color)} />
                </div>
                
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{dept.name}</span>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        'text-xs gap-1 border-0',
                        methodInfo.bgColor,
                        methodInfo.color
                      )}
                    >
                      <MethodIcon className="h-3 w-3" />
                      {methodInfo.label}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span className={cn(
                        dept.onlineAgentsCount > 0 ? 'text-emerald-600' : 'text-muted-foreground'
                      )}>
                        {dept.onlineAgentsCount}/{dept.agentsCount} online
                      </span>
                    </div>
                    
                    {dept.maxConversationsPerAgent > 0 && (
                      <span>
                        Limite: {dept.maxConversationsPerAgent} conv/agente
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Último agente */}
              {dept.lastAssignedAgentName && (
                <div className="text-right shrink-0">
                  <span className="text-xs text-muted-foreground">Último:</span>
                  <p className="text-sm font-medium text-foreground">{dept.lastAssignedAgentName}</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Paginação - exibe apenas se houver mais de uma página */}
        <SimplePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
